import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { tap } from 'rxjs';
import { ENVIRONMENT } from '@base/lib/core/tokens/environment';
import { Role } from '@base/lib/core/services/permission';

export interface GetUserProfileResponse {
  id: number;
  account: string;
  identityNumber: string;
  name: string;
  birthday: string;
  phone: string;
  tel?: string;
  email?: string;
  sex: 'Male' | 'Female' | 'Other';
  age: number;
  imageUrl: string;
  recordDate: string;
  isCooperatorMember: boolean;
  cooperatorId: number;
  cooperatorName: string;
  userCertification: {
    generalMember: boolean;
    identityCertifiedMember: boolean;
    medicalSystemCertifiedMember: boolean;
    hrc: boolean;
  };
  roles: {
    roleCode: Role;
    roleName: string;
    hospitalOrganizationId: number;
    hospitalOrganizationCode: string;
    hospitalOrganizationName: string;
    serviceLocationCode: string | null;
    serviceLocationName: string | null;
    canUseWebService: boolean | null;
  }[];
  userDetail: {
    informationConsent?: boolean;
    healthPassbook?: boolean;
    agreeOrganizationSwitchData?: boolean;
  };
  userAddresses: {
    city: string;
    district: string;
    street: string;
    kind: number;
  }[];
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private http = inject(HttpClient);
  private readonly environment = inject(ENVIRONMENT);

  readonly baseUrl = signal(this.environment.api.baseUrl).asReadonly();

  readonly userId = computed(() => this._GetUserProfileResponse()?.id ?? 0);
  readonly userName = computed(() => this._GetUserProfileResponse()?.name ?? '尚未登入');
  readonly identityNumber = computed(() => this._GetUserProfileResponse()?.identityNumber);
  readonly roleList = computed(() => this._GetUserProfileResponse()?.roles);

  readonly userProfile = computed(() => this._GetUserProfileResponse());

  private _GetUserProfileResponse = signal<GetUserProfileResponse | null>(null);

  apiGetUserProfile() {
    return this.http.get<GetUserProfileResponse>(
      `${this.baseUrl()}/api/User/profile`,
    ).pipe(
      tap((res) => {
        this._GetUserProfileResponse.set(res);
      }),
    );
  }
}
